import { fetchAuthSession } from 'aws-amplify/auth'

interface CachedToken {
  token: string
  expiresAt: number
}

interface TokenFetchPromise {
  promise: Promise<string>
  timestamp: number
}

export class SessionExpiredError extends Error {
  constructor(message = 'Session has expired and requires re-authentication') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

class AuthTokenCache {
  private cachedToken: CachedToken | null = null
  private fetchPromise: TokenFetchPromise | null = null
  // Cache for 4 minutes (240 seconds) when tokens live 5 minutes (300 seconds)
  // This gives us 1 minute buffer before token expires
  private readonly CACHE_DURATION_MS = 4 * 60 * 1000
  private readonly PROMISE_TIMEOUT_MS = 10 * 1000

  async getToken(): Promise<string> {
    const now = Date.now()

    if (this.cachedToken && now < this.cachedToken.expiresAt) {
      return this.cachedToken.token
    }

    if (this.fetchPromise) {
      if (now - this.fetchPromise.timestamp > this.PROMISE_TIMEOUT_MS) {
        this.fetchPromise = null
      } else {
        try {
          return await this.fetchPromise.promise
        } catch {
          this.fetchPromise = null
        }
      }
    }

    const fetchPromise = this.fetchTokenFromAWS()
    this.fetchPromise = {
      promise: fetchPromise,
      timestamp: now
    }

    try {
      const token = await fetchPromise

      // Try to get actual token expiry from JWT, fallback to default cache duration
      const tokenExpiry = this.parseTokenExpiry(token)
      const cacheDurationMs = tokenExpiry
        ? this.calculateCacheDuration(tokenExpiry)
        : this.CACHE_DURATION_MS

      this.cachedToken = {
        token,
        expiresAt: now + cacheDurationMs
      }

      this.fetchPromise = null

      return token
    } catch (error) {
      this.fetchPromise = null
      throw error
    }
  }

  private parseTokenExpiry(token: string): number | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3 || !parts[1]) {
        return null
      }
      const payload = JSON.parse(atob(parts[1])) as { exp?: number }
      return payload.exp || null
    } catch {
      return null
    }
  }

  private calculateCacheDuration(tokenExpiryTimestamp: number): number {
    const now = Math.floor(Date.now() / 1000)
    const tokenLifetimeSeconds = tokenExpiryTimestamp - now

    if (tokenLifetimeSeconds <= 0) {
      console.warn('⚠️ [AUTH_CACHE] Token is already expired!')
      return 0
    }

    // Cache for 80% of token lifetime, but at least 30 seconds and at most 4 minutes
    const cacheSeconds = Math.max(30, Math.min(240, Math.floor(tokenLifetimeSeconds * 0.8)))

    console.info(
      `🕐 [AUTH_CACHE] Token expires in ${tokenLifetimeSeconds}s, caching for ${cacheSeconds}s`
    )
    return cacheSeconds * 1000
  }

  private async fetchTokenFromAWS(): Promise<string> {
    try {
      console.info('🔄 [AUTH_CACHE] Fetching fresh token from AWS Cognito...')
      const session = await fetchAuthSession()

      console.info('🔍 [AUTH_CACHE] Session received:', {
        hasSession: !!session,
        hasTokens: !!session?.tokens,
        hasAccessToken: !!session?.tokens?.accessToken,
        hasIdToken: !!session?.tokens?.idToken
      })

      const token = session?.tokens?.accessToken?.toString()

      if (!token) {
        console.warn('⚠️ [AUTH_CACHE] No access token available - session may have expired')
        throw new SessionExpiredError('No access token available from AWS Cognito')
      }

      console.info('✅ [AUTH_CACHE] Successfully obtained fresh token')
      return token
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        console.error('🚨 [AUTH_CACHE] Session expired:', error.message)
        throw error
      }

      console.error('❌ [AUTH_CACHE] Failed to fetch auth session:', error)
      throw new SessionExpiredError(
        `Failed to fetch auth session: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  clearCache(): void {
    console.info('🧹 [AUTH_CACHE] Clearing token cache')
    this.cachedToken = null
    this.fetchPromise = null
  }

  handleCrossTabLogout(): void {
    console.info('🚨 [AUTH_CACHE] Cross-tab logout detected - clearing cache')
    this.clearCache()
  }
}

export const authTokenCache = new AuthTokenCache()
